import {
  Checkbox,
  DatePicker,
  Grid,
  GridColumn,
  GridRow,
  Input,
  ModalBottomButtons,
  Select,
  SelectOptionType,
} from '@bp/ui-components';
import dayjs, { Dayjs } from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemorizedCacheTags } from '../../../hooks/useMemorizedCacheTag';
import {
  UpdateAbsenceInput,
  useAbsenceQuery,
  useCreateAbsenceMutation,
  useUpdateAbsenceMutation,
} from '../../../types/planung-graphql-client-defs';
import { AbsenceReason, getAbsenceReasonSelectOptions } from '../../../utils/absenceReasons';
import {
  combineFirstDateWithSecondTime,
  isFirstAfterSecond,
  isFirstBeforeSecond,
  isSame,
  setTimeStringOnDate,
} from '../../../utils/dateCalculations';
import { showErrorToast, showSuccessCreateToast, showSuccessUpdateToast } from '../../../utils/toast';
import { useSubstitutionStore } from '../../Substitutions/SubstitutionProvider';

export type TeacherAbsenceFormType = {
  uuid: string;
  teacherUuid: string;
  startDate: Dayjs;
  startTime: Dayjs;
  endDate: Dayjs;
  endTime: Dayjs;
  reason: AbsenceReason;
  comment: string;
  removeSubstitutions: boolean;
};

type TeacherAbsenceFormProps = {
  selectedAbsenceUuid?: string | null;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const TeacherAbsenceForm = observer(({ selectedAbsenceUuid, onLoad, onClose }: TeacherAbsenceFormProps) => {
  const { t } = useTranslation();
  const { selectedDay } = useSubstitutionStore();
  const context = useMemorizedCacheTags(['ABSENCES', 'EVENT']);

  const [, create] = useCreateAbsenceMutation();
  const [, update] = useUpdateAbsenceMutation();

  const [{ data }] = useAbsenceQuery({
    variables: { uuid: selectedAbsenceUuid ?? '' },
    pause: !selectedDay || !selectedAbsenceUuid,
  });

  const { teacherData } = useLoadBasicData({ pause: !selectedDay });

  const reasonOpts = getAbsenceReasonSelectOptions();
  const teacherOpts = useCreateSelectOptions(teacherData?.people, 'uuid', 'listName');

  // Ohne uuid (neue Abwesenheit) pausiert die Abfrage; ein älteres Ergebnis darf dann nicht vorbelegen.
  const absence = selectedAbsenceUuid ? data?.absence : null;
  const selectedAbsence = absence
    ? {
        uuid: absence.uuid,
        teacherUuid: absence.personUuid,
        startDate: dayjs(absence.start),
        startTime: dayjs(absence.start),
        endDate: dayjs(absence.end),
        endTime: dayjs(absence.end),
        reason: absence.reason,
        comment: absence.comment,
      }
    : null;

  const initialValues: TeacherAbsenceFormType = useMemo(() => {
    return {
      uuid: selectedAbsence?.uuid ?? '',
      teacherUuid: selectedAbsence?.teacherUuid ?? '',
      startDate: selectedAbsence?.startDate ? dayjs(selectedAbsence.startDate) : selectedDay,
      startTime: selectedAbsence?.startTime ? dayjs(selectedAbsence.startTime) : selectedDay.startOf('day'),
      endDate: selectedAbsence?.endDate ? dayjs(selectedAbsence.endDate) : selectedDay,
      endTime: selectedAbsence?.endTime ? dayjs(selectedAbsence.endTime) : selectedDay.endOf('day'),
      reason: selectedAbsence?.reason ?? AbsenceReason.Illness,
      comment: selectedAbsence?.comment ?? '',
      removeSubstitutions: !!selectedAbsenceUuid,
    };
  }, [
    selectedAbsence?.comment,
    selectedAbsence?.endDate,
    selectedAbsence?.endTime,
    selectedAbsence?.reason,
    selectedAbsence?.startDate,
    selectedAbsence?.startTime,
    selectedAbsence?.teacherUuid,
    selectedAbsence?.uuid,
    selectedAbsenceUuid,
    selectedDay,
  ]);

  async function handleSubmit(values: TeacherAbsenceFormType, formHelpers: FormikHelpers<TeacherAbsenceFormType>) {
    onLoad(true);

    if (!selectedAbsenceUuid) {
      const { error } = await create(
        {
          input: {
            person: values.teacherUuid,
            reason: values.reason,
            start: combineFirstDateWithSecondTime(values.startDate, values.startTime).toISOString(),
            end: combineFirstDateWithSecondTime(values.endDate, values.endTime).toISOString(),
            comment: values.comment,
          },
        },
        context,
      );

      if (error) {
        showErrorToast(error);
      } else {
        showSuccessCreateToast();
      }
    } else {
      // only send changed values. removeSubstitutions is a special case and should be sent if any date or time is changed
      const updateInput: UpdateAbsenceInput = { removeSubstitutions: false };
      if (values.comment !== initialValues.comment) {
        updateInput.comment = values.comment;
      }
      if (values.reason !== initialValues.reason) {
        updateInput.reason = values.reason;
      }
      if (
        !values.startDate.isSame(initialValues.startDate, 'day') ||
        !values.startTime.isSame(initialValues.startTime, 'minutes')
      ) {
        updateInput.start = combineFirstDateWithSecondTime(values.startDate, values.startTime).toISOString();
        updateInput.removeSubstitutions = values.removeSubstitutions;
      }
      if (
        !values.endDate.isSame(initialValues.endDate, 'day') ||
        !values.endTime.isSame(initialValues.endTime, 'minutes')
      ) {
        updateInput.end = combineFirstDateWithSecondTime(values.endDate, values.endTime).toISOString();
        updateInput.removeSubstitutions = values.removeSubstitutions;
      }
      const { error } = await update(
        {
          input: {
            uuids: [selectedAbsenceUuid],
          },
          update: updateInput,
        },
        context,
      );
      if (error) {
        showErrorToast(error);
      } else {
        showSuccessUpdateToast();
      }
    }

    onLoad(false);
    formHelpers.resetForm();
    onClose();
  }

  const validate = (values: TeacherAbsenceFormType) => {
    const errors: Record<string, string> = {};
    if (!values.teacherUuid || values.teacherUuid === '') {
      errors.teacherUuid = t('validation.common.required');
    }
    if (isFirstAfterSecond(values.startDate, values.endDate, 'day')) {
      errors.startDate = t('validation.common.startDateAfterEnd');
      errors.endDate = t('validation.common.startDateAfterEnd');
    }
    if (
      isSame(values.startDate, values.endDate, 'day') &&
      isFirstAfterSecond(values.startTime, values.endTime, 'minute')
    ) {
      errors.startTime = t('validation.common.startTimeAfterEnd');
      errors.endTime = t('validation.common.startTimeAfterEnd');
    }
    return errors;
  };

  return (
    <Formik<TeacherAbsenceFormType>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validate={validate}
      validateOnChange={true}
      validateOnBlur={true}
      enableReinitialize={true}
    >
      {({ errors, values, setFieldValue, isSubmitting, dirty, isValidating, handleSubmit }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingBottom='s'>
                <GridColumn width={6}>
                  <Select
                    disabled={!!selectedAbsenceUuid}
                    name='teacherUuid'
                    isSearchable
                    label={t('persons.title', { count: 1 })}
                    value={teacherOpts.find((t) => t.value === values?.teacherUuid)}
                    options={teacherOpts}
                    onChange={async (option) => {
                      const opt = option as SingleValue<SelectOptionType>;
                      await setFieldValue('teacherUuid', opt?.value);
                    }}
                    menuPosition='fixed'
                    error={errors.teacherUuid}
                  />
                </GridColumn>
                <GridColumn width={6}>
                  <Select
                    name='reason'
                    label={t('absences.reason')}
                    value={reasonOpts.find((r) => r.value === values?.reason)}
                    options={reasonOpts}
                    onChange={async (option) => {
                      const opt = option as SingleValue<SelectOptionType>;
                      await setFieldValue('reason', opt?.value);
                    }}
                    menuPosition='fixed'
                    error={errors.reason}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingBottom='s' spacingTop='s'>
                <GridColumn width={3}>
                  <DatePicker
                    name='startDate'
                    value={values.startDate.toDate()}
                    label={t('common.from')}
                    onChange={async (e) => {
                      await setFieldValue('startDate', dayjs(e));
                    }}
                    error={errors.startDate as string}
                  />
                </GridColumn>
                <GridColumn width={3}>
                  <Input
                    name='startTime'
                    label={t('common.start')}
                    value={dayjs(values.startTime).format('HH:mm')}
                    onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                      const time = setTimeStringOnDate(values.startTime, event.target.value);
                      await setFieldValue('startTime', dayjs(time));
                    }}
                    type='time'
                    error={errors.startTime as string}
                  />
                </GridColumn>
                <GridColumn width={3}>
                  <DatePicker
                    name='endDate'
                    value={values.endDate.toDate()}
                    label={t('common.until')}
                    onChange={async (e) => {
                      await setFieldValue('endDate', dayjs(e));
                    }}
                    error={errors.endDate as string}
                  />
                </GridColumn>
                <GridColumn width={3}>
                  <Input
                    name='endTime'
                    label={t('common.end')}
                    value={dayjs(values.endTime).format('HH:mm')}
                    onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                      const time = setTimeStringOnDate(values.endTime, event.target.value);
                      await setFieldValue('endTime', dayjs(time));
                    }}
                    type='time'
                    error={errors.endTime as string}
                  />
                </GridColumn>
              </GridRow>
              <GridRow spacingTop='s'>
                <Input
                  name='comment'
                  value={values.comment}
                  label={t('common.comment')}
                  onChange={async (e) => await setFieldValue('comment', e.target.value)}
                  error={errors.comment}
                />
              </GridRow>
              {selectedAbsenceUuid && (
                <GridRow spacingTop='s'>
                  <Checkbox
                    disabled={true}
                    name='removeSubstitutions'
                    checked={true}
                    label={t('absences.removeSubstitutionsOutOfNewRange')}
                    onChange={async (e) => await setFieldValue('removeSubstitutions', e.target.checked)}
                    error={errors.comment}
                  />
                </GridRow>
              )}
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: onClose,
              }}
              submitButton={{
                disabled: isSubmitting || !dirty || isValidating,
                callback: () => {
                  handleSubmit();
                },
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
});
